import React, { useState } from 'react';
import { useSettings } from '../context/SettingsContext';
import { X, Percent, DollarSign, TrendingUp, Lock } from 'lucide-react';

const MarginSettings = ({ onClose }) => {
  const {
    showMarginPrice,
    setShowMarginPrice,
    showWholesalePrice,
    setShowWholesalePrice,
    desiredMarginPercentage,
    setDesiredMarginPercentage
  } = useSettings();

  const [tempMargin, setTempMargin] = useState(desiredMarginPercentage);

  const handleMarginChange = (value) => {
    // Ensure whole numbers only between 0-40
    const numValue = Math.round(Math.max(0, Math.min(40, parseFloat(value) || 0)));
    setTempMargin(numValue);
  };

  const handleSave = () => {
    setDesiredMarginPercentage(tempMargin);
    onClose();
  };

  const quickSelectValues = [10, 15, 20, 25, 30];

  return (
    <div className="margin-settings-overlay">
      <div className="margin-settings-modal">
        <div className="modal-header">
          <h2>
            <Percent size={20} />
            Margin Settings
          </h2>
          <button onClick={onClose} className="close-btn">
            <X size={24} />
          </button>
        </div>

        <div className="modal-content">
          {/* Margin Percentage Section */}
          <div className="settings-section">
            <h3>Desired Margin Percentage</h3>
            <div className="margin-input-group">
              <label>Margin:</label>
              <input
                type="number"
                value={tempMargin}
                onChange={(e) => handleMarginChange(e.target.value)}
                min="0"
                max="40"
                step="1"
                className="margin-input"
              />
              <span>%</span>
            </div>

            {/* Slider */}
            <div className="margin-slider">
              <input
                type="range"
                min="0"
                max="40"
                step="1"
                value={tempMargin}
                onChange={(e) => handleMarginChange(e.target.value)}
                className="slider"
              />
              <div className="slider-labels">
                <span>0%</span>
                <span className="current-value">{tempMargin}%</span>
                <span>40%</span>
              </div>
            </div>

            {/* Quick Select Buttons */}
            <div className="quick-select">
              <label>Quick Select:</label>
              <div className="quick-select-buttons">
                {quickSelectValues.map(value => (
                  <button
                    key={value}
                    onClick={() => setTempMargin(value)}
                    className={`quick-btn ${tempMargin === value ? 'active' : ''}`}
                  >
                    {value}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Display Options */}
          <div className="settings-section">
            <h3>Display Options</h3>
            <div className="toggle-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={showMarginPrice}
                  onChange={(e) => setShowMarginPrice(e.target.checked)}
                />
                <span>
                  <DollarSign size={16} />
                  Show Sale Price
                </span>
              </label>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={showWholesalePrice}
                  onChange={(e) => setShowWholesalePrice(e.target.checked)}
                />
                <span>
                  <TrendingUp size={16} />
                  Show Wholesale Price
                </span>
              </label>
            </div>
          </div>

          {/* Sample Calculation */}
          <div className="settings-section">
            <h3>Sample Calculation</h3>
            <div className="calculation-example">
              <div className="calc-row">
                <span>Wholesale:</span>
                <span>$100,000</span>
              </div>
              <div className="calc-row">
                <span>+ Margin ({tempMargin}%):</span>
                <span>${(100000 * tempMargin / 100).toLocaleString()}</span>
              </div>
              <div className="calc-row total">
                <span>Sale Price:</span>
                <span>${(100000 + (100000 * tempMargin / 100)).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Information */}
          <div className="settings-info">
            <p>
              <Lock size={14} />
              Margin range: 0% to 40% (whole numbers only)
            </p>
            <p>
              <Lock size={14} />
              This margin will be applied to all wholesale prices to calculate sale price
            </p>
            <p>
              <Lock size={14} />
              Only visible to you - not shown to customers
            </p>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarginSettings;
